/* FinalCTA — última oportunidad de conversión antes del footer.
   Pantalla casi completa, azul de marca pleno, un único momento visual:
   título + stores. Sin distracciones, sin ruido competitivo. */

.section {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: var(--blue-50);
  color: var(--color-text);
  padding-block: var(--space-24);
  min-height: 100vh;
  display: flex;
  align-items: center;
}

@media (min-width: 768px) {
  .section {
    padding-block: var(--space-32);
  }
}

/* ─── Inner — entra con scale + fade cuando llega a viewport ─── */

.inner {
  position: relative;
  text-align: center;
  max-width: 720px;

  opacity: 0;
  transform: scale(0.95);
  transition:
    opacity 600ms var(--ease-decelerate),
    transform 700ms var(--ease-decelerate);
  will-change: transform, opacity;
}

.revealed {
  opacity: 1;
  transform: scale(1);
}

/* ─── Heading — momento culminante, peso pleno ─── */

.title {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: clamp(34px, 6vw, 64px);
  line-height: 1.05;
  letter-spacing: -0.025em;
  color: var(--color-text);
  margin: 0 0 var(--space-4);
}

.subtitle {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: var(--text-lg);
  line-height: 1.55;
  color: var(--color-text-muted);
  margin: 0 0 var(--space-10);
}

@media (min-width: 768px) {
  .subtitle {
    margin-bottom: var(--space-12);
    font-size: var(--text-xl);
  }
}

/* ─── Stores — par de pills blancos. Visualmente unificados: el CTA único. ─── */

.stores {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  justify-content: center;
  align-items: center;
  margin-bottom: var(--space-8);
}

@media (min-width: 560px) {
  .stores {
    flex-direction: row;
    gap: var(--space-4);
  }
}

.store {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  background: var(--color-surface);
  color: var(--color-text);
  border: 0;
  border-radius: var(--radius-full);
  padding: 14px 26px 14px 22px;
  min-height: 60px;
  min-width: 220px;
  font: inherit;
  text-decoration: none;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  box-shadow: var(--shadow-lg);
  transition:
    transform var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard);
}

@media (min-width: 768px) {
  .store {
    padding: 16px 32px 16px 26px;
    min-height: 68px;
    min-width: 240px;
  }
}

.store:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-xl);
}

.store:active {
  transform: translateY(0) scale(0.98);
}

.storeIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  flex-shrink: 0;
}

.storeText {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
  text-align: left;
}

.storeKicker {
  font-family: var(--font-body);
  font-size: var(--text-xs);
  font-weight: 500;
  color: var(--color-text-muted);
  letter-spacing: 0.02em;
  margin-bottom: 2px;
}

.storeName {
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-lg);
  letter-spacing: -0.01em;
  color: var(--color-text);
}

/* ─── Coming soon — Google Play todavía no está publicada. Botón (no link):
   más liviano que el de App Store (la CTA viva) y con un tag ámbar "Pronto".
   Al tocarlo revela el aviso .androidNote. ─── */

.comingSoon {
  box-shadow: var(--shadow-md);
}

.comingSoon .storeName {
  color: var(--color-text-muted);
}

.badge {
  margin-left: auto;
  align-self: center;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--amber-700);
  background: var(--amber-50);
  padding: 3px 9px;
  border-radius: var(--radius-full);
  line-height: 1.3;
  white-space: nowrap;
}

/* Aviso que aparece al tocar el botón de Android. role="status" + aria-live
   → los lectores de pantalla lo anuncian al revelarse. Reserva alto para no
   empujar el link secundario al togglear. */

.androidNote {
  min-height: 1.5rem;
  margin: 0 0 var(--space-4);
  font-family: var(--font-body);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--amber-700);
}

.androidNoteInner {
  display: inline-block;
  animation: noteIn var(--duration-base) var(--ease-decelerate);
}

@keyframes noteIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Pulse — halo expansivo. 4 ciclos al entrar viewport, luego se detiene.
   Aplica a los dos botones simultáneamente para que se lean como una sola CTA. */
@keyframes storePulse {
  0%,
  100% {
    box-shadow: var(--shadow-lg), 0 0 0 0 rgba(46, 108, 200, 0.35);
  }
  50% {
    box-shadow: var(--shadow-lg), 0 0 0 16px rgba(46, 108, 200, 0);
  }
}

.storesPulse .storeAvailable {
  animation: storePulse 2.6s var(--ease-standard) 0.8s 4;
}

/* Si interactúan con un botón, paramos el pulse de ambos (no es ruido en hover) */
.storesPulse:hover .store,
.storesPulse:focus-within .store {
  animation: none;
}

/* ─── Secondary link — pequeño, para no competir con la CTA principal ─── */

.secondary {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-body);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--color-text-muted);
  text-decoration: none;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  transition: color var(--duration-fast) var(--ease-standard);
}

.secondary:hover {
  color: var(--color-primary);
}

.secondaryArrow {
  display: inline-block;
  transition: transform var(--duration-base) var(--ease-standard);
}

.secondary:hover .secondaryArrow {
  transform: translateX(3px);
}

/* ─── Reduced motion — desactivamos todas las animaciones de movimiento ─── */

@media (prefers-reduced-motion: reduce) {
  .inner {
    opacity: 1;
    transform: none;
    transition: none;
  }
  .storesPulse .storeAvailable {
    animation: none;
  }
  .store {
    transition: box-shadow var(--duration-base) var(--ease-standard);
  }
  .store:hover,
  .store:active {
    transform: none;
  }
  .androidNoteInner {
    animation: none;
  }
  .secondaryArrow {
    transition: none;
  }
  .secondary:hover .secondaryArrow {
    transform: none;
  }
}
